/** A printed card's marker (STORY-089 M2): the 7 × 7 module grid and its SVG.
 *
 * The grid is a one-module black border around the card's 5 × 5 data word (`codebook.ts`),
 * drawn with A at the top. A white quiet zone one module wide surrounds it on paper; the
 * decoder needs it clear to find the border. */
import { CODEBOOK } from "./codebook";

export const CARD_COUNT = CODEBOOK.length;
/** Modules across the marker, border included. */
export const GRID = 7;

/** The card's word for card `n` (1 to `CARD_COUNT`). */
export function cardWord(n: number): number {
  const w = CODEBOOK[n - 1];
  if (w === undefined || !Number.isInteger(n)) throw new RangeError(`no card ${n}`);
  return w;
}

/** `grid[r][c]` is true where the module is black; row 0 is the A edge. */
export function markerGrid(n: number): boolean[][] {
  const word = cardWord(n);
  const grid: boolean[][] = [];
  for (let r = 0; r < GRID; r++) {
    const row: boolean[] = [];
    for (let c = 0; c < GRID; c++) {
      const border = r === 0 || c === 0 || r === GRID - 1 || c === GRID - 1;
      row.push(border || ((word >>> ((r - 1) * 5 + (c - 1))) & 1) === 1);
    }
    grid.push(row);
  }
  return grid;
}

/** The black modules as one SVG path in module units, the marker's top-left corner at
 * (`x`, `y`). Runs of black in a row become one rectangle, so the printer gets no hairline
 * seams between modules. */
export function markerPath(n: number, x = 0, y = 0): string {
  const grid = markerGrid(n);
  const parts: string[] = [];
  grid.forEach((row, r) => {
    let c = 0;
    while (c < GRID) {
      if (!row[c]) {
        c++;
        continue;
      }
      let end = c;
      while (end < GRID && row[end]) end++;
      parts.push(`M${x + c} ${y + r}h${end - c}v1h${c - end}z`);
      c = end;
    }
  });
  return parts.join("");
}

/** A standalone SVG of the marker and its quiet zone (9 × 9 modules), for anything that wants
 * the card as a file. Black on white whatever the theme: it is ink on paper. */
export function markerSvg(n: number, moduleMm = 18): string {
  const size = (GRID + 2) * moduleMm;
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" width="${size}mm" height="${size}mm" viewBox="0 0 ${GRID + 2} ${GRID + 2}" shape-rendering="crispEdges">` +
    `<rect width="${GRID + 2}" height="${GRID + 2}" fill="#fff"/><path fill="#000" d="${markerPath(n, 1, 1)}"/></svg>`
  );
}
